import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { PiCheckBold } from 'react-icons/pi';
import { ParticlePortrait } from './ParticlePortrait';
import { SplitWords } from './SplitWords';
import { EASE } from '../lib/motion';
import me from '../assets/me.jpg';

/**
 * About the person behind the studio. The portrait is a live particle field
 * (see PortraitScene) that gathers itself as the section scrolls in.
 */
export function Studio() {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();
  const points = [t('studio.point1'), t('studio.point2'), t('studio.point3')];

  return (
    <section id="studio" className="relative px-5 py-28 md:px-8 md:py-44">
      <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12 lg:gap-8">
        <figure className="lg:col-span-6">
          <ParticlePortrait
            src={me}
            alt={t('studio.team.kostas.name')}
            className="mx-auto block aspect-square w-full max-w-[620px]"
          />
          <figcaption className="mx-auto mt-2 flex max-w-[620px] flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
            <span className="font-display text-lg font-semibold tracking-tight">
              {t('studio.team.kostas.name')}
            </span>
            <span className="text-sm text-accent">
              {t('studio.team.kostas.role')}
            </span>
          </figcaption>
        </figure>

        <div className="lg:col-span-5 lg:col-start-8">
          <h2 className="max-w-[16ch] font-display text-5xl font-semibold leading-[1] tracking-[-0.04em] md:text-6xl">
            <SplitWords text={t('studio.title')} stagger={0.05} />
          </h2>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mt-7 max-w-[50ch] text-lg leading-relaxed text-muted"
          >
            {t('studio.body')}
          </motion.p>

          <ul className="mt-10 grid gap-5">
            {points.map((p, i) => (
              <motion.li
                key={p}
                initial={reduce ? false : { opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.8 }}
                transition={{
                  duration: 0.7,
                  ease: EASE,
                  delay: 0.3 + i * 0.08,
                }}
                className="flex items-start gap-4"
              >
                <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full border border-accent/40 text-accent">
                  <PiCheckBold className="text-xs" />
                </span>
                <span className="text-[15px] text-fg/90">{p}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
